import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  intoSnakeCase,
} from '../../../fetchers/_helpers';
import {
  AlternateAudioTrackFields,
  AlternateAudioTrack,
} from '../../../models/components/schemas/alternate-audio-track/AlternateAudioTrack';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoVersionAudiotracksFields = readonly AlternateAudioTrackFields[];

/** Options for GET /videos/{video_id}/versions/{version_id}/audiotracks */
export type GetVideoVersionAudiotracksOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video.audio_track+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the video version.
     */
    versionId: number;
  };
};

/** Result type for GET /videos/{video_id}/versions/{version_id}/audiotracks */
export type GetVideoVersionAudiotracksResult<Paths extends readonly string[]> = Select<
  AlternateAudioTrack,
  Paths
>[];

/**
 * Get all audio tracks for a video version
 * This method returns all audio tracks that are associated with the specified video version.
 */
export async function getVideoVersionAudiotracks<Paths extends GetVideoVersionAudiotracksFields>({
  baseUrl,
  select,
  where: { videoId, versionId },
  ...options
}: GetVideoVersionAudiotracksOptions<Paths>): Promise<GetVideoVersionAudiotracksResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/versions/${versionId}/audiotracks?fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetVideoVersionAudiotracksResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PostVideoVersionAudiotracksFields = readonly AlternateAudioTrackFields[];

/** Options for POST /videos/{video_id}/versions/{version_id}/audiotracks */
export type PostVideoVersionAudiotracksOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.video.audio_track+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the video version.
     */
    versionId: number;
  };

  /** Data serialized to body for POST /videos/{video_id}/versions/{version_id}/audiotracks */
  variables: {
    /**
     * Whether the audio track is enabled.
     * @example true
     */

    active?: boolean;

    /**
     * The language of the audio track. For a full list of supported languages, use the [`/languages?filter=audiotracks`](https://developer.vimeo.com/api/reference/videos#get_languages) method.
     * @example en-US
     */

    languageCode?: string;

    /**
     * The origin of the audio track.
     *
     * Option descriptions:
     *  * `ai_generated` - The audio track is generated by AI.
     *  * `user_uploaded` - The audio track is uploaded by a user.
     *  * `user_uploaded_and_ai_generated` - The audio track is uploaded by a user and contains AI content.
     *
     */

    provenance?: `ai_generated` | `user_uploaded` | `user_uploaded_and_ai_generated`;

    /**
     * The type of audio track.
     *
     * Option descriptions:
     *  * `commentary` - The audio track is the commentary type.
     *  * `descriptive` - The audio track is the descriptive type.
     *  * `main` - The audio track is the main type.
     *
     */

    type?: `commentary` | `descriptive` | `main`;

    upload: {
      /**
       * The type of upload approach. For more information about upload approaches, refer to [Working with Video Uploads](https://developer.vimeo.com/api/upload/videos#understanding-upload-approaches).
       *
       * Option descriptions:
       *  * `post` - The upload approach is POST.
       *  * `pull` - The upload approach is pull.
       *  * `tus` - The upload approach is tus.
       *
       */

      approach: `post` | `pull` | `tus`;

      /**
       * The public URL at which the video is hosted. The URL must be valid for at least 24 hours. Use this parameter when **approach** is `pull`.
       * @example https://example.com/video.mp4
       */

      link?: string;

      /**
       * The size in bytes of the asset to upload. The maximum value of this field is `268435456000`, which corresponds to 250 GB.
       * @example 13623861
       */

      size?: string;
    };
  };
};

/** Result type for POST /videos/{video_id}/versions/{version_id}/audiotracks */
export type PostVideoVersionAudiotracksResult<Paths extends readonly string[]> = Select<
  AlternateAudioTrack,
  Paths
>;

/**
 * Create an audio track for a video version
 * This method creates a new audio track for a video version.
 */
export async function postVideoVersionAudiotracks<Paths extends PostVideoVersionAudiotracksFields>({
  baseUrl,
  select,
  variables,
  where: { videoId, versionId },
  ...options
}: PostVideoVersionAudiotracksOptions<Paths>): Promise<PostVideoVersionAudiotracksResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/versions/${versionId}/audiotracks?fields=${select
      .map(intoSnakeCase)
      .join(',')}`,
    {
      ...options,
      method: 'POST',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PostVideoVersionAudiotracksResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
